import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { deleteChargeMutation } from '@/graphql/mutations/deleteCharge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface DeleteChargeModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  chargeId: string;
  chargeName?: string;
  onSuccess?: () => void;
}

/**
 * Modal for confirming and handling charge deletion via GraphQL.
 * This component handles the GraphQL mutation and provides feedback.
 */
const DeleteChargeModal: React.FC<DeleteChargeModalProps> = ({
  isOpen,
  onOpenChange,
  chargeId,
  chargeName = 'this charge',
  onSuccess,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleDelete = async () => {
    if (!chargeId) return;
    
    setIsSubmitting(true);
    
    try {
      console.log(`🔄 [DeleteChargeModal] Deleting charge ${chargeId}`);
      
      // Call the GraphQL mutation to delete the charge
      const response = await deleteChargeMutation(chargeId);
      
      if (response.deleteCharge.success) {
        toast({
          title: "Charge deleted",
          description: `Successfully deleted ${chargeName}`,
          variant: "default",
        });
        
        // Close the modal and notify parent component
        onOpenChange(false);
        if (onSuccess) onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.deleteCharge.message || "Failed to delete charge",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('❌ [DeleteChargeModal] Error deleting charge:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Delete Charge
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete {chargeName}? This action cannot be undone and will remove the charge from all versions.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            disabled={isSubmitting}
            className="bg-red-600 hover:bg-red-700"
          >
            {isSubmitting ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteChargeModal;
